import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Avatar } from "../components/Avatar.js";
import { Button, Notice } from "../components/ui.js";
import { useAuth } from "../lib/auth.js";
import {
  fetchMySeeding,
  fetchPanelEventIds,
  saveSeeding,
  saveSeedingDraftOnLeave,
} from "../lib/seeding.js";
import { moveBy, place, skip, unrank, unskip, type Groups } from "../lib/seeding-order.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { MySeeding, Tournament, TournamentEvent } from "../lib/types.js";

/**
 * Seeding, for a panelist ordering a pool. The organiser's side, the only place the average is
 * shown, is TournamentSeeding.tsx.
 *
 * Nothing here is the lock. The database sends a panelist only their own list, nobody but an
 * organiser the average, and refuses a write from anybody else; the screens only decline to
 * show what would not work.
 */

/** Pools the signed-in member is on the panel for. */
function usePanelEvents(memberId: string | undefined) {
  return useQuery({
    queryKey: ["seeding-panel-events", memberId],
    queryFn: () => fetchPanelEventIds(memberId!),
    enabled: memberId !== undefined,
  });
}

function seedingLocked(tournament: Tournament, event: TournamentEvent): boolean {
  return event.seedingClosedAt !== null || tournament.auctionStartedAt !== null;
}

/**
 * A panelist's way in, on the tournament page: one row per pool they are on the panel for.
 * Nothing for anybody else, which is nearly everybody.
 */
export function SeedingLinks({ tournament }: { tournament: Tournament }) {
  const { member } = useAuth();
  const { data: panelEvents = [] } = usePanelEvents(tournament.auction ? member?.id : undefined);
  const pools = tournament.events.filter((event) => panelEvents.includes(event.id));

  if (!tournament.auction || pools.length === 0) return null;

  return (
    <section className="mt-6 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-1">Seeding</h2>
      <ul>
        {pools.map((event) => (
          <li
            key={event.id}
            className="border-b"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <Link
              to={`/tournaments/${tournament.id}/seeding/${event.id}`}
              className="flex min-h-[56px] items-center gap-3 py-2"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-base font-bold">Rank {event.name}</span>
                <span className="font-mono text-[11px] text-ink-muted">
                  {seedingLocked(tournament, event) ? "Seeding is closed" : "You are on the panel"}
                </span>
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                aria-hidden
                className="shrink-0"
              >
                <path d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The panelist's page for one pool: rank or skip every player, then submit. */
export function SeedingRank() {
  const { tournamentId, eventId } = useParams<{ tournamentId: string; eventId: string }>();
  const { member } = useAuth();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });
  const { data: panelEvents, isPending: panelPending } = usePanelEvents(member?.id);

  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);
  const event = tournament?.events.find((candidate) => candidate.id === eventId);

  if (isPending || panelPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament || !event) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error ? "Couldn't load this pool. Try again." : "That pool isn't here any more."}
        </Notice>
      </main>
    );
  }

  // Signposting: the database sends a member who is not on the panel nothing at all.
  if (!tournament.auction || !panelEvents?.includes(event.id)) {
    return <Navigate to={`/tournaments/${tournament.id}`} replace />;
  }

  return <RankList tournament={tournament} event={event} />;
}

function RankList({ tournament, event }: { tournament: Tournament; event: TournamentEvent }) {
  const queryClient = useQueryClient();
  const locked = seedingLocked(tournament, event);
  const { data: mine, isPending } = useQuery({
    queryKey: ["my-seeding", event.id],
    queryFn: () => fetchMySeeding(event.id),
  });

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6 md:max-w-4xl">
      <Link to={`/tournaments/${tournament.id}`} className="micro mb-4 inline-block underline">
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow={`Seeding · ${tournament.name}`} title={event.name} />

      {isPending || !mine ? (
        <p className="micro">Loading…</p>
      ) : (
        // Keyed on the saved list, so a submission or a registration change starts the editor
        // over from what the database now holds. An autosave does not refetch, so it does not
        // start it over: the panelist's selection and place on the page stay put.
        <Editor
          key={
            mine.players.map((p) => `${p.memberId}:${p.state}`).join(",") + String(mine.submitted)
          }
          eventId={event.id}
          mine={mine}
          locked={locked}
          onSubmitted={() =>
            void queryClient.invalidateQueries({ queryKey: ["my-seeding", event.id] })
          }
        />
      )}
    </main>
  );
}

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; message: string };

/**
 * Saves a draft shortly after the panelist stops changing it, one save at a time, so the list the
 * database ends with is always the last one on screen. A pending save is sent at once when the
 * page is hidden or the editor goes away, rather than lost; when the page itself is going away it
 * goes by `leave`, which needs nothing awaited to get out.
 */
function useAutosave(save: (groups: Groups) => Promise<void>, leave: (groups: Groups) => void) {
  const [state, setState] = useState<SaveState>({ kind: "idle" });
  const latest = useRef<Groups | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;
  const leaveRef = useRef(leave);
  leaveRef.current = leave;

  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (inFlight.current !== null || latest.current === null) return;

    const groups = latest.current;
    latest.current = null;
    setState({ kind: "saving" });
    inFlight.current = saveRef.current(groups).then(
      () => setState({ kind: "saved" }),
      (error: unknown) =>
        setState({
          kind: "failed",
          message: error instanceof Error ? error.message : String(error),
        }),
    );
    void inFlight.current.finally(() => {
      inFlight.current = null;
      // Something changed while that save was out: send it now.
      if (latest.current !== null) flush();
    });
  }, []);

  const schedule = useCallback(
    (groups: Groups) => {
      latest.current = groups;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(flush, 600);
    },
    [flush],
  );

  /** Drop anything not yet sent and wait for a save already out, before a submission. */
  const settle = useCallback(async () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    latest.current = null;
    while (inFlight.current !== null) await inFlight.current;
  }, []);

  useEffect(() => {
    const hidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    const gone = () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      if (latest.current !== null) leaveRef.current(latest.current);
      latest.current = null;
    };
    window.addEventListener("pagehide", gone);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("pagehide", gone);
      document.removeEventListener("visibilitychange", hidden);
      flush();
    };
  }, [flush]);

  return { state, schedule, settle };
}

/**
 * While `active`, warns before the page is closed or reloaded, and before an in-app link is
 * followed. The app's router cannot block navigation, so links are caught on the way down, in
 * the capture phase, before the router sees the click. The browser's back button is not caught.
 */
function useLeaveWarning(active: boolean) {
  useEffect(() => {
    if (!active) return;

    const message = "Your changes have not been submitted. Leave and lose them?";
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const click = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!anchor || anchor.getAttribute("target") === "_blank") return;
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", click, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", click, true);
    };
  }, [active]);
}

function sameGroups(a: Groups, b: Groups): boolean {
  return (
    a.ranked.join(",") === b.ranked.join(",") &&
    [...a.skipped].sort().join(",") === [...b.skipped].sort().join(",")
  );
}

/**
 * The panelist's list, built by picking: tap a player in the pool and they take the next place.
 * A player can also be put in any place, skipped, moved a place at a time, or sent back.
 *
 * A draft saves itself. A submitted list does not: changes to it wait for Resubmit, so the list
 * that counts never turns back into a draft because of a stray tap.
 */
function Editor({
  eventId,
  mine,
  locked,
  onSubmitted,
}: {
  eventId: string;
  mine: MySeeding;
  locked: boolean;
  onSubmitted: () => void;
}) {
  const byId = useMemo(() => new Map(mine.players.map((p) => [p.memberId, p])), [mine]);
  // Name order, the neutral order the pool and the skipped are kept in.
  const neutral = useMemo(
    () =>
      [...mine.players]
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((p) => p.memberId),
    [mine],
  );
  const loaded = useMemo<Groups>(() => {
    const where = (state: string) => mine.players.filter((p) => p.state === state);
    return {
      ranked: where("ranked").map((p) => p.memberId),
      skipped: neutral.filter((id) => byId.get(id)?.state === "skipped"),
      pool: neutral.filter((id) => byId.get(id)?.state === "unplaced"),
    };
  }, [mine, neutral, byId]);

  const [groups, setGroups] = useState<Groups>(loaded);
  const [selected, setSelected] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const sheetHeight = useHeight<HTMLElement>();

  const autosave = useAutosave(
    (next) => saveSeeding(eventId, next.ranked, false, next.skipped),
    (next) => saveSeedingDraftOnLeave(eventId, next.ranked, next.skipped),
  );
  const unsent = mine.submitted && !sameGroups(groups, loaded);
  useLeaveWarning(unsent);

  const submit = useMutation({
    mutationFn: async () => {
      await autosave.settle();
      await saveSeeding(eventId, groups.ranked, true, groups.skipped);
    },
    onSuccess: onSubmitted,
  });

  useEffect(() => {
    if (selected === null) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [selected]);

  if (mine.players.length === 0) {
    return <Notice>Nobody is registered in this pool yet, so there is nothing to rank.</Notice>;
  }

  const change = (next: Groups) => {
    if (next === groups) return;
    setGroups(next);
    setSelected(null);
    setMenuFor(null);
    if (!mine.submitted) autosave.schedule(next);
  };
  const busy = submit.isPending;
  const total = mine.players.length;
  const placed = groups.ranked.length + groups.skipped.length;
  const nextPlace = groups.ranked.length + 1;
  const ready = groups.pool.length === 0 && groups.ranked.length > 0;
  const name = (id: string) => byId.get(id)?.displayName ?? "Someone";

  return (
    <div>
      <p className="mb-4 text-sm text-ink-muted">
        Tap the strongest player left to give them the next place. Skip anybody you can&rsquo;t
        judge; a skip doesn&rsquo;t count towards their average. Only the organisers see your list
        and the panel&rsquo;s average.
      </p>

      {locked ? (
        <Notice>Seeding is closed, so this list can no longer be changed.</Notice>
      ) : mine.outOfDate ? (
        <Notice tone="attention">
          Somebody registered or withdrew since you submitted, so your list does not count. Place
          anybody new and submit again.
        </Notice>
      ) : mine.submitted ? (
        <Notice>Submitted. You can change it until seeding closes.</Notice>
      ) : null}

      <div className="md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-start md:gap-8">
        <section aria-label="Your ranking">
          <ol>
            {groups.ranked.map((memberId, index) => {
              const player = byId.get(memberId)!;
              return (
                <Fragment key={memberId}>
                  {selected !== null ? (
                    <Gap
                      label={`Place ${name(selected)} at ${index + 1}`}
                      onClick={() => change(place(groups, selected, index))}
                    />
                  ) : null}
                  <li
                    className="flex min-h-[52px] items-center gap-3 border-b py-1.5"
                    style={{ borderColor: "var(--color-line-muted)" }}
                  >
                    <span className="font-mono w-6 shrink-0 text-[13px] font-semibold">
                      {index + 1}
                    </span>
                    <Avatar name={player.displayName} url={player.avatarUrl} size={28} />
                    <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">
                      {player.displayName}
                    </span>
                    {!locked ? (
                      <span className="flex shrink-0">
                        <RowButton
                          label={`Move ${player.displayName} up`}
                          disabled={busy || index === 0}
                          onClick={() =>
                            change({ ...groups, ranked: moveBy(groups.ranked, index, -1) })
                          }
                        >
                          <path d="M6 15l6-6 6 6" />
                        </RowButton>
                        <RowButton
                          label={`Move ${player.displayName} down`}
                          disabled={busy || index === groups.ranked.length - 1}
                          onClick={() =>
                            change({ ...groups, ranked: moveBy(groups.ranked, index, 1) })
                          }
                        >
                          <path d="M6 9l6 6 6-6" />
                        </RowButton>
                        <RowButton
                          label={`Send ${player.displayName} back`}
                          disabled={busy}
                          onClick={() => change(unrank(groups, memberId, neutral))}
                        >
                          <path d="M6 6l12 12M18 6L6 18" />
                        </RowButton>
                      </span>
                    ) : null}
                  </li>
                </Fragment>
              );
            })}
            {!locked && groups.pool.length > 0 ? (
              selected !== null ? (
                <Gap
                  label={`Place ${name(selected)} at ${nextPlace}`}
                  onClick={() => change(place(groups, selected))}
                />
              ) : (
                <li
                  className="flex min-h-[52px] items-center gap-3 border-b border-dashed py-1.5 text-ink-muted"
                  style={{ borderColor: "var(--color-line-muted)" }}
                >
                  <span className="font-mono w-6 shrink-0 text-[13px] font-semibold">
                    {nextPlace}
                  </span>
                  <span className="text-[15px]">Next</span>
                </li>
              )
            ) : null}
          </ol>

          {locked && groups.skipped.length > 0 ? (
            <p className="mt-3 text-sm text-ink-muted">
              <span className="font-mono text-[11px] uppercase tracking-[0.06em]">Skipped</span>{" "}
              {groups.skipped.map(name).join(", ")}
            </p>
          ) : null}
        </section>

        {!locked ? (
          // On a phone this sheet is pinned above the tab bar, so the players to pick stay in
          // reach however long the ranking grows. Fixed rather than sticky: a sticky sheet cannot
          // rise above the top of the ranking, and with a short ranking under the page header it
          // ran off the bottom of the screen. From md up it is a column beside the ranking.
          <aside
            ref={sheetHeight.ref}
            aria-label="Players to place"
            className="fixed inset-x-0 bottom-[calc(74px+env(safe-area-inset-bottom))] z-[5] border-t-2 bg-ground px-4 pb-3 pt-3 md:sticky md:inset-x-auto md:top-6 md:bottom-auto md:border-t-0 md:px-0 md:pt-0"
            style={{ borderColor: "var(--color-line)" }}
          >
            <div className="mx-auto max-w-2xl md:max-w-none">
              <div className="mb-2 flex items-baseline justify-between gap-3">
                <h2 className="text-[15px] font-bold" aria-live="polite">
                  {selected !== null
                    ? `Where does ${name(selected)} go?`
                    : groups.pool.length > 0
                      ? `Who's #${nextPlace}?`
                      : "Everyone is placed"}
                </h2>
                <span className="font-mono shrink-0 text-[11px] text-ink-muted">
                  {placed} of {total} placed
                </span>
              </div>

              <div className="max-h-[28vh] overflow-y-auto md:max-h-none">
                {selected !== null ? (
                  <p className="mb-2 text-sm text-ink-muted">
                    Tap a place in your ranking, or tap {name(selected)} again to cancel.
                  </p>
                ) : null}
                {groups.pool.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {groups.pool.map((memberId) => (
                      <PoolChip
                        key={memberId}
                        name={name(memberId)}
                        selected={selected === memberId}
                        menuOpen={menuFor === memberId}
                        disabled={busy}
                        onPick={() => {
                          if (selected === memberId) setSelected(null);
                          else if (selected !== null) setSelected(memberId);
                          else change(place(groups, memberId));
                        }}
                        onMenu={() => setMenuFor(menuFor === memberId ? null : memberId)}
                        onPlaceAt={() => {
                          setMenuFor(null);
                          setSelected(memberId);
                        }}
                        onSkip={() => change(skip(groups, memberId, neutral))}
                      />
                    ))}
                  </ul>
                ) : null}

                {groups.skipped.length > 0 ? (
                  <details open className="mt-3">
                    <summary className="font-mono cursor-pointer text-[11px] uppercase tracking-[0.06em] text-ink-muted">
                      Skipped ({groups.skipped.length})
                    </summary>
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {groups.skipped.map((memberId) => (
                        <li key={memberId}>
                          <button
                            type="button"
                            disabled={busy}
                            aria-label={`Bring ${name(memberId)} back`}
                            onClick={() => change(unskip(groups, memberId, neutral))}
                            className="max-w-[12rem] truncate border-2 border-dashed px-3 text-sm text-ink-muted line-through disabled:opacity-40"
                            style={{
                              minHeight: 44,
                              borderRadius: "var(--radius-control)",
                              borderColor: "var(--color-line-muted)",
                            }}
                          >
                            {name(memberId)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  tone="primary"
                  disabled={busy || !ready || (mine.submitted && !unsent && !mine.outOfDate)}
                  onClick={() => submit.mutate()}
                >
                  {mine.submitted ? "Resubmit" : "Submit"}
                </Button>
                <span className="font-mono text-[11px] text-ink-muted" aria-live="polite">
                  {unsent
                    ? "Changes not submitted"
                    : autosave.state.kind === "saving"
                      ? "Saving…"
                      : autosave.state.kind === "saved"
                        ? "Draft saved"
                        : null}
                </span>
              </div>
              {autosave.state.kind === "failed" ? (
                <Notice tone="attention">Couldn&rsquo;t save: {autosave.state.message}</Notice>
              ) : null}
              {submit.error ? <Notice tone="attention">{submit.error.message}</Notice> : null}
            </div>
          </aside>
        ) : null}
      </div>
      {/* Room to scroll the last of the ranking clear of the pinned sheet. */}
      {!locked ? (
        <div aria-hidden className="md:hidden" style={{ height: sheetHeight.height }} />
      ) : null}
    </div>
  );
}

/** The rendered height of an element, kept up to date as it changes. */
function useHeight<T extends HTMLElement>() {
  const [height, setHeight] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((element: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (element === null) return;
    observer.current = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.current.observe(element);
  }, []);
  return { ref, height };
}

/** A place in the ranking a selected player can be put into. */
function Gap({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="font-mono my-1 flex min-h-[44px] w-full items-center justify-center border-2 border-dashed text-[11px] uppercase tracking-[0.06em]"
        style={{ borderColor: "var(--color-line)", borderRadius: "var(--radius-control)" }}
      >
        {label}
      </button>
    </li>
  );
}

/**
 * A player waiting to be placed. Tapping the name takes the next place; the ⋯ beside it opens
 * the two other things to do with them, inline, so nothing floats over the list on a phone.
 */
function PoolChip({
  name,
  selected,
  menuOpen,
  disabled,
  onPick,
  onMenu,
  onPlaceAt,
  onSkip,
}: {
  name: string;
  selected: boolean;
  menuOpen: boolean;
  disabled: boolean;
  onPick: () => void;
  onMenu: () => void;
  onPlaceAt: () => void;
  onSkip: () => void;
}) {
  const box = {
    minHeight: 44,
    borderRadius: "var(--radius-control)",
    border: "2px solid var(--color-line)",
  } as const;

  return (
    <li className="flex">
      <button
        type="button"
        disabled={disabled}
        aria-pressed={selected}
        onClick={onPick}
        className="max-w-[12rem] truncate px-3 text-sm font-semibold disabled:opacity-40"
        style={{
          ...box,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          background: selected ? "var(--color-line)" : "transparent",
          color: selected ? "var(--color-ground)" : "var(--color-ink)",
        }}
      >
        {name}
      </button>
      {menuOpen ? (
        <>
          <button
            type="button"
            disabled={disabled}
            onClick={onPlaceAt}
            className="px-3 text-sm disabled:opacity-40"
            style={{ ...box, borderLeft: "none", borderRadius: 0 }}
          >
            Place…
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={onSkip}
            className="px-3 text-sm disabled:opacity-40"
            style={{ ...box, borderLeft: "none", borderRadius: 0 }}
          >
            Don&rsquo;t know
          </button>
        </>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        aria-expanded={menuOpen}
        aria-label={menuOpen ? `Close options for ${name}` : `More for ${name}`}
        onClick={onMenu}
        className="w-9 text-sm text-ink-muted disabled:opacity-40"
        style={{ ...box, borderLeft: "none", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      >
        {menuOpen ? "×" : "⋯"}
      </button>
    </li>
  );
}

function RowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-11 items-center justify-center disabled:opacity-30"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        aria-hidden
      >
        {children}
      </svg>
    </button>
  );
}
